import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Target, Send, Plus, Trash2, Handshake, CircleDollarSign, CheckCircle2, Lock, Copy, Users, Link2, Landmark, Trophy, Award } from "lucide-react";
import api, { formatApiErrorDetail, safeCopy } from "@/lib/api";
import PortalNav from "@/components/PortalNav";
import SalesAcademy from "@/components/sales/SalesAcademy";
import { AcademyCertificateButton } from "@/components/sales/AcademyCertificate";
import EmailVerifyCard from "@/components/EmailVerifyCard";
import SalesNotifications from "@/components/sales/SalesNotifications";
import SalesGuideWiki from "@/components/sales/SalesGuideWiki";
import SalesKpi from "@/components/sales/SalesKpi";
import SalesTools from "@/components/sales/SalesTools";
import SalesHUD from "@/components/sales/SalesHUD";
import SalesDealRooms from "@/components/sales/SalesDealRooms";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const LEAD_STATUS = {
  new: "text-nc-orange border-nc-orange/40 bg-nc-orange/10",
  assigned: "text-nc-blue border-nc-blue/40 bg-nc-blue/10",
  scoped: "text-nc-green border-nc-green/40 bg-nc-green/10",
  closed: "text-zinc-400 border-white/15 bg-white/5",
};
const COMM_STATUS = {
  locked: { cls: "text-zinc-400 border-white/15 bg-white/5", icon: Lock },
  claimable: { cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10", icon: CircleDollarSign },
  paid: { cls: "text-nc-green border-nc-green/40 bg-nc-green/10", icon: CheckCircle2 },
};

function ScopeBuilder({ lead, onSaved, onClose }) {
  const [items, setItems] = useState(lead.scope?.length ? lead.scope : [{ service: "", detail: "", price: "" }]);
  const [note, setNote] = useState(lead.scope_note || "");
  const [busy, setBusy] = useState(false);
  const total = items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  const save = async () => {
    setBusy(true);
    try {
      const clean = items.filter((i) => i.service.trim()).map((i) => ({ ...i, price: parseFloat(i.price) || 0 }));
      const r = await api.post(`/sales/leads/${lead.id}/scope`, { items: clean, note });
      toast.success(`Scope saved — AUD ${r.data.total.toFixed(2)}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="scope-builder" className="border border-white/10 bg-[#050505]/60 p-4 flex flex-col gap-3">
      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">Scope Builder</p>
      {items.map((it, i) => (
        <div key={i} data-testid={`scope-item-${i + 1}`} className="grid grid-cols-[1fr_90px_28px] gap-2 items-start">
          <div className="flex flex-col gap-1.5">
            <input className={inputCls} placeholder="Service (e.g. UE5 Environment)" value={it.service} onChange={(e) => setItem(i, "service", e.target.value)} />
            <input className={inputCls} placeholder="Detail (optional)" value={it.detail || ""} onChange={(e) => setItem(i, "detail", e.target.value)} />
          </div>
          <input className={inputCls} placeholder="AUD" type="number" min="0" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} />
          <button data-testid={`scope-remove-${i + 1}`} onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))} className="h-9 border border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-400/40 flex items-center justify-center transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button data-testid="scope-add-item" onClick={() => setItems((arr) => [...arr, { service: "", detail: "", price: "" }])} className="self-start font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 flex items-center gap-1.5 transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add line item
      </button>
      <textarea rows={2} className={`${inputCls} resize-none`} placeholder="Scope note for the client (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex items-center justify-between border-t border-white/10 pt-3">
        <p className="font-mono text-xs text-zinc-400">TOTAL <span data-testid="scope-total" className="text-nc-green font-bold">AUD {total.toFixed(2)}</span></p>
        <button data-testid="scope-save" disabled={busy || !items.some((i) => i.service.trim())} onClick={save} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
          Save Scope
        </button>
      </div>
      {lead.status === "scoped" && (
        <button data-testid="open-close-deal" onClick={onClose} className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors flex items-center justify-center gap-2">
          <Handshake className="w-3.5 h-3.5" /> Close Deal → Create Project
        </button>
      )}
    </div>
  );
}

function CloseDealForm({ lead, onDone, onCancel }) {
  const [title, setTitle] = useState(`${lead.name} — Co-Development Project`);
  const [engine, setEngine] = useState("Unreal Engine 5");
  const [budget, setBudget] = useState(String(lead.scope_total || ""));
  const [milestones, setMilestones] = useState([{ title: "Milestone 1 — Kickoff", description: "", price: "", due_date: "" }]);
  const [isRetainer, setIsRetainer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const setMs = (i, k, v) => setMilestones((arr) => arr.map((m, j) => (j === i ? { ...m, [k]: v } : m)));

  const closeDeal = async () => {
    setBusy(true);
    try {
      const clean = milestones.filter((m) => m.title.trim()).map((m) => ({ ...m, price: parseFloat(m.price) || 0 }));
      const r = await api.post(`/sales/leads/${lead.id}/close`, { title, engine_type: engine, budget_total: parseFloat(budget) || 0, milestones: clean, is_retainer: isRetainer });
      setResult(r.data);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div data-testid="close-deal-success" className="border border-nc-green/40 bg-nc-green/5 p-4 flex flex-col gap-3">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Deal Closed — Project Live</p>
        <p className="font-mono text-[11px] text-zinc-300 leading-relaxed">
          The client workspace is active and the conversation has moved to a client sync channel. Your commission is locked in and unlocks as milestones are paid.
        </p>
        {result.temp_password && (
          <div className="border border-nc-orange/40 bg-nc-orange/5 p-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase mb-1.5">Client login credentials (hand over securely)</p>
            <p className="font-mono text-[11px] text-zinc-200">{lead.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <p data-testid="client-temp-password" className="font-mono text-[11px] text-nc-orange">{result.temp_password}</p>
              <button data-testid="copy-temp-password" onClick={async () => { (await safeCopy(result.temp_password)) ? toast.success("Copied") : toast.error("Copy blocked — note it down manually"); }} className="text-zinc-400 hover:text-white transition-colors"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}
        <button data-testid="close-deal-done" onClick={onCancel} className="self-start font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors">Back to queue</button>
      </div>
    );
  }

  return (
    <div data-testid="close-deal-form" className="border border-nc-orange/30 bg-nc-orange/5 p-4 flex flex-col gap-3">
      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Handshake className="w-3.5 h-3.5" /> Close Deal</p>
      <div>
        <label className={labelCls}>Project title *</label>
        <input data-testid="close-title-input" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Engine</label>
          <select data-testid="close-engine-select" className={inputCls} value={engine} onChange={(e) => setEngine(e.target.value)}>
            {["Unreal Engine 5", "Unity", "CryEngine", "Enfusion", "Other"].map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Budget total (AUD) *</label>
          <input data-testid="close-budget-input" type="number" min="1" className={inputCls} value={budget} onChange={(e) => setBudget(e.target.value)} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Milestones</label>
        {milestones.map((m, i) => (
          <div key={i} data-testid={`milestone-row-${i + 1}`} className="grid grid-cols-[1fr_80px_110px_28px] gap-2 mb-2">
            <input className={inputCls} placeholder="Milestone title" value={m.title} onChange={(e) => setMs(i, "title", e.target.value)} />
            <input className={inputCls} placeholder="AUD" type="number" min="0" value={m.price} onChange={(e) => setMs(i, "price", e.target.value)} />
            <input className={inputCls} placeholder="Due (e.g. Q3 2026)" value={m.due_date} onChange={(e) => setMs(i, "due_date", e.target.value)} />
            <button onClick={() => setMilestones((arr) => arr.filter((_, j) => j !== i))} className="h-9 border border-white/10 text-zinc-500 hover:text-red-400 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button data-testid="milestone-add" onClick={() => setMilestones((arr) => [...arr, { title: "", description: "", price: "", due_date: "" }])} className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 flex items-center gap-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add milestone
        </button>
      </div>
      <div className="flex gap-2">
        <button data-testid="close-deal-submit" disabled={busy || title.trim().length < 3 || !(parseFloat(budget) > 0)} onClick={closeDeal} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
          {busy ? "Closing…" : "Close & Create Workspace"}
        </button>
        <button onClick={onCancel} className="font-mono text-[9px] uppercase text-zinc-400 px-3">Cancel</button>
      </div>
      <label data-testid="close-retainer-toggle" className="flex items-start gap-2.5 font-mono text-[10px] text-zinc-300 cursor-pointer border border-nc-green/30 bg-nc-green/5 px-3 py-2.5">
        <input type="checkbox" checked={isRetainer} onChange={(e) => setIsRetainer(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#00994d]" />
        <span><span className="text-nc-green font-bold uppercase tracking-[0.15em] text-[9px]">Recurring retainer</span> — client pays monthly; you earn commission on EVERY payment, for life</span>
      </label>
    </div>
  );
}

export default function SalesPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [pageTab, setPageTab] = useState("pipeline"); // pipeline | guide
  const [earnings, setEarnings] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        if (!["sales", "admin"].includes(r.data.role)) { navigate("/"); return; }
        setMe(r.data);
      })
      .catch(() => { localStorage.removeItem("nc_token"); navigate("/auth"); });
  }, [navigate]);

  const load = useCallback(() => {
    api.get("/sales/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/portal/commissions").then((r) => setCommissions(r.data)).catch(() => {});
    api.get("/sales/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
    api.get("/sales/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/sales/leaderboard").then((r) => setLeaderboard(r.data)).catch(() => {});
  }, []);

  const completeStep = async (stepId) => {
    setBusy(true);
    try {
      const r = await api.post(`/sales/onboarding/${stepId}/complete`);
      toast.success(r.data.done ? "Sales Academy complete — lead queue unlocked" : "Step completed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (me) load(); }, [me, load]);

  const pollChat = useCallback(async () => {
    if (!active) return;
    try {
      const r = await api.get(`/sales/chat/${active.channel_id}`);
      setMessages(r.data.messages);
    } catch { /* ignore */ }
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    pollChat();
    const id = setInterval(pollChat, 3500);
    return () => clearInterval(id);
  }, [pollChat, active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const claim = async (lead) => {
    setBusy(true);
    try {
      await api.post(`/sales/leads/${lead.id}/claim`);
      toast.success("Lead assigned to you");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!draft.trim() || !active) return;
    try {
      await api.post(`/sales/chat/${active.channel_id}`, { text: draft.trim(), guest_name: "" });
      setDraft("");
      pollChat();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const payout = async (cid) => {
    try {
      await api.post(`/admin/commissions/${cid}/payout`);
      toast.success("Commission paid out");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!me) return <div className="min-h-screen bg-[#050505]" />;

  const openLeads = leads.filter((l) => l.status !== "closed");

  return (
    <div data-testid="sales-page" className="min-h-screen bg-[#050505] text-zinc-100 pt-6 pb-16 px-4 sm:px-6">
      <div className="max-w-[1500px] mx-auto flex flex-col gap-5">
        <PortalNav title="Sales Dashboard" accent="text-nc-orange" />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-mono text-[9px] tracking-[0.35em] text-nc-orange uppercase">Specpixel // Revenue Operations</p>
            <h1 className="font-sans font-bold text-2xl uppercase tracking-wider mt-1 flex items-center gap-3"><Target className="w-6 h-6 text-nc-orange" /> Sales Dashboard</h1>
          </div>
          <div className="flex gap-4 font-mono text-[10px] text-zinc-500 uppercase items-center">
            <span>Open leads <span data-testid="stat-open-leads" className="text-nc-orange">{openLeads.length}</span></span>
            <span>Closed <span className="text-nc-green">{leads.length - openLeads.length}</span></span>
            <SalesNotifications me={me} />
          </div>
        </div>

        <div data-testid="sales-page-tabs" className="flex gap-2 border-b border-white/10 pb-3">
          {[["pipeline", "Pipeline"], ["guide", "Master Guide"], ["tools", "AI Tools"]].map(([t, label]) => (
            <button key={t} data-testid={`sales-tab-${t}`} onClick={() => setPageTab(t)} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 border transition-colors ${pageTab === t ? "border-nc-orange/60 text-nc-orange bg-nc-orange/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{label}</button>
          ))}
        </div>

        {pageTab === "tools" ? (
          <SalesTools />
        ) : pageTab === "guide" ? (
          <SalesGuideWiki link={`${window.location.origin}/?rec=${me.recruitment_id}`} />
        ) : (<>

        {onboarding && !onboarding.done && me.role !== "admin" && (
          <>
            <SalesAcademy onboarding={onboarding} onComplete={completeStep} busy={busy} />
            <button data-testid="open-master-guide" onClick={() => setPageTab("guide")} className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-blue hover:text-zinc-100 transition-colors">
              Training backup // open the full Master Guide wiki
            </button>
          </>
        )}

        {onboarding?.done && me.role === "sales" && (
          <div data-testid="sales-academy-done" className="border border-nc-green/40 bg-nc-green/5 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-nc-green shrink-0" strokeWidth={1.75} />
              <p className="font-mono text-[11px] text-zinc-300"><span className="text-nc-green font-bold uppercase tracking-[0.2em] text-[10px]">Sales Academy complete.</span> Your certificate of completion is ready.</p>
            </div>
            <AcademyCertificateButton path="/sales/onboarding/certificate/pdf" filename="specpixel-sales-academy-certificate.pdf" testid="sales-certificate-download" />
          </div>
        )}

        {me.role === "sales" && !me.email_verified_at && me.created_at >= "2026-08-10" && (
          <EmailVerifyCard email={me.email} onVerified={() => window.location.reload()} testid="sales-email-verify" />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Earnings card */}
          <div data-testid="sales-earnings-card" className="border border-white/10 bg-black/40">
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2">
              <Landmark className="w-3.5 h-3.5" /> Your Earnings (net of platform fee)
            </div>
            <div className="p-4 grid grid-cols-3 gap-2 text-center">
              <div className="border border-white/10 px-2 py-3">
                <p data-testid="earnings-locked" className="font-sans font-bold text-lg text-zinc-300">AUD {(earnings?.locked ?? 0).toFixed(2)}</p>
                <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Locked</p>
              </div>
              <div className="border border-nc-green/30 bg-nc-green/5 px-2 py-3">
                <p data-testid="earnings-claimable" className="font-sans font-bold text-lg text-nc-green">AUD {(earnings?.claimable ?? 0).toFixed(2)}</p>
                <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Claimable</p>
              </div>
              <div className="border border-white/10 px-2 py-3">
                <p data-testid="earnings-paid" className="font-sans font-bold text-lg text-zinc-300">AUD {(earnings?.paid ?? 0).toFixed(2)}</p>
                <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">Paid Out</p>
              </div>
            </div>
            <p className="px-4 pb-3 font-mono text-[9px] text-zinc-600 leading-relaxed">
              Commission locks when you close a deal, becomes claimable as the client pays milestones, and is paid out after admin approval. Specpixel's platform fee is already deducted.
            </p>
          </div>

          {/* Leaderboard card */}
          <div data-testid="sales-leaderboard-card" className="border border-white/10 bg-black/40">
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center justify-between">
              <span className="flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> Rep Leaderboard</span>
              <span className="text-zinc-600 normal-case tracking-normal">{leaderboard?.month || ""}</span>
            </div>
            <div className="p-4 flex flex-col gap-2">
              {(leaderboard?.entries || []).length === 0 && (
                <p className="font-mono text-[10px] text-zinc-600 py-4 text-center">No reps on the board yet this month.</p>
              )}
              {(leaderboard?.entries || []).slice(0, 5).map((e) => (
                <div
                  key={e.rank}
                  data-testid={`leaderboard-row-${e.rank}`}
                  className={`flex items-center gap-3 border px-3 py-2 font-mono text-[11px] ${e.you ? "border-nc-orange/50 bg-nc-orange/10" : "border-white/10"}`}
                >
                  <span className={`font-sans font-bold text-sm w-6 ${e.rank === 1 ? "text-nc-orange" : "text-zinc-500"}`}>#{e.rank}</span>
                  <span className={`flex-1 truncate ${e.you ? "text-nc-orange font-bold" : "text-zinc-200"}`}>{e.name}{e.you ? " (you)" : ""}</span>
                  <span className="text-zinc-500 shrink-0">{e.deals} {e.deals === 1 ? "deal" : "deals"}</span>
                  <span className="text-nc-green font-bold shrink-0">AUD {e.net.toFixed(0)}</span>
                </div>
              ))}
              <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em] mt-1">Resets monthly // ranked by net commission earned</p>
            </div>
          </div>

          {/* Personal link card */}
          <div data-testid="sales-link-card" className="border border-nc-orange/30 bg-nc-orange/5">
            <div className="border-b border-nc-orange/20 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
              <Link2 className="w-3.5 h-3.5" /> Your Personal Lead Link
            </div>
            <div className="p-4">
              <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
                Share this link anywhere — prospects who open the sales chat through it are <span className="text-nc-orange">auto-assigned to you</span> as self-sourced leads. Same commission, no claim race.
              </p>
              <div className="mt-3 flex items-center gap-2 border border-white/15 bg-black/40 px-3 py-2">
                <p data-testid="sales-personal-link" className="font-mono text-[11px] text-nc-orange truncate flex-1">
                  {window.location.origin}/?rec={me.recruitment_id}
                </p>
                <button
                  data-testid="copy-personal-link"
                  onClick={async () => { (await safeCopy(`${window.location.origin}/?rec=${me.recruitment_id}`)) ? toast.success("Personal link copied") : toast.error("Copy blocked — copy manually"); }}
                  className="text-zinc-400 hover:text-white shrink-0 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <SalesKpi />

        <SalesDealRooms leads={leads} onOpenChat={(lead) => { setActive(lead); setClosing(false); setTimeout(() => document.querySelector("[data-testid='sales-chat-messages']")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); }} />

        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr_380px] gap-5 items-start">
          {/* Lead queue */}
          <div className="border border-white/10 bg-black/40">
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Lead Queue ({leads.length})</div>
            {me.role !== "admin" && (
              <p data-testid="queue-privacy-note" className="px-4 py-2 border-b border-white/10 font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase flex items-center gap-1.5"><Lock className="w-3 h-3" /> Private pipeline — unclaimed leads + yours only</p>
            )}
            <div className="max-h-[620px] overflow-y-auto">
              {leads.length === 0 && <p data-testid="leads-empty" className="font-mono text-[10px] text-zinc-600 p-4">No leads yet — the prospect uplink on the public site feeds this queue.</p>}
              {leads.map((l, i) => (
                <div key={l.id} data-testid={`lead-row-${i + 1}`} onClick={() => { setActive(l); setClosing(false); }} className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${active?.id === l.id ? "bg-nc-blue/10 border-l-2 border-l-nc-blue" : "hover:bg-white/5"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{l.name}</p>
                    <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 shrink-0 ${LEAD_STATUS[l.status] || LEAD_STATUS.new}`}>{l.status}</span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 truncate mt-0.5">{l.email}</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="font-mono text-[9px] text-zinc-600">{l.message_count || 0} msgs // {String(l.created_at).slice(0, 10)}</p>
                    {(l.status === "new" || !l.sales_rep_id) && (
                      <button data-testid={`lead-claim-${i + 1}`} disabled={busy || (onboarding && !onboarding.done && me.role !== "admin")} title={onboarding && !onboarding.done && me.role !== "admin" ? "Complete the Sales Academy first" : ""} onClick={(e) => { e.stopPropagation(); claim(l); }} className="font-mono text-[8px] tracking-[0.15em] uppercase px-2 py-1 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors disabled:opacity-40">
                        Claim
                      </button>
                    )}
                    {l.source === "self_sourced" && (
                      <span data-testid={`lead-selfsourced-${i + 1}`} className="font-mono text-[8px] tracking-[0.15em] uppercase px-1.5 py-0.5 border border-nc-orange/40 text-nc-orange bg-nc-orange/10">self-sourced</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat */}
          <div className="border border-white/10 bg-black/40 flex flex-col min-h-[500px]">
            <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">
              {active ? `Channel // ${active.name}` : "Select a lead"}
            </div>
            {active ? (
              <>
                <div data-testid="sales-chat-messages" className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5 max-h-[480px]">
                  {messages.map((m) => {
                    const staff = m.sender_kind === "sales";
                    return (
                      <div key={m.id} className={`max-w-[80%] px-3 py-2 border ${staff ? "self-end bg-nc-blue/10 border-nc-blue/30" : "self-start bg-white/5 border-white/10"}`}>
                        <p className={`font-mono text-[8px] tracking-[0.2em] uppercase mb-1 ${staff ? "text-nc-blue" : "text-nc-orange"}`}>{m.sender_name} // {m.sender_kind}</p>
                        <p className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <div className="border-t border-white/10 p-3 flex gap-2">
                  <input data-testid="sales-chat-input" className={inputCls} placeholder="Reply as studio…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
                  <button data-testid="sales-chat-send" onClick={send} disabled={!draft.trim()} className="shrink-0 w-11 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white flex items-center justify-center transition-colors"><Send className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <p className="font-mono text-[10px] text-zinc-600 p-6">Pick a lead from the queue to open the tracked conversation.</p>
            )}
          </div>

          {/* Scope + close */}
          <div className="flex flex-col gap-4">
            {active && active.status !== "closed" ? (
              closing ? (
                <CloseDealForm lead={active} onDone={load} onCancel={() => { setClosing(false); setActive(null); }} />
              ) : (
                <ScopeBuilder lead={active} onSaved={load} onClose={() => setClosing(true)} />
              )
            ) : (
              <div className="border border-white/10 bg-[#050505]/60 p-4">
                <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-2">Scope Builder</p>
                <p className="font-mono text-[10px] text-zinc-600 leading-relaxed">Select an open lead to build its scope, then close the deal to spin up the client workspace, milestones and your commission.</p>
              </div>
            )}
            {active && (
              <div className="border border-white/10 bg-[#050505]/60 p-4 font-mono text-[10px] text-zinc-500 leading-relaxed">
                <p className="tracking-[0.3em] text-zinc-400 uppercase mb-2">Lead Intel</p>
                <p>Status: <span className="text-zinc-200">{active.status}</span></p>
                <p>Rep: <span className="text-zinc-200">{active.sales_rep_email || "unclaimed"}</span></p>
                {active.scope_total != null && <p>Scope: <span className="text-nc-green">AUD {Number(active.scope_total).toFixed(2)}</span></p>}
              </div>
            )}
          </div>
        </div>

        {/* Commissions ledger */}
        <div className="border border-white/10 bg-black/40">
          <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2"><CircleDollarSign className="w-3.5 h-3.5" /> Commission Ledger ({commissions.length})</div>
          {commissions.length === 0 && <p data-testid="commissions-empty" className="font-mono text-[10px] text-zinc-600 p-4">No commissions yet — close a deal to lock one in.</p>}
          {commissions.map((cm, i) => {
            const st = COMM_STATUS[cm.status] || COMM_STATUS.locked;
            return (
              <div key={cm.id} data-testid={`commission-row-${i + 1}`} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5 font-mono text-[11px] flex-wrap">
                <span className="text-zinc-300 truncate">{cm.sales_rep_email}</span>
                <span className="text-zinc-500 shrink-0">{cm.percentage}% of project</span>
                <span className="text-zinc-400 shrink-0">Gross AUD {Number(cm.total_amount).toFixed(2)}</span>
                {cm.fee_amount != null && <span className="text-nc-orange shrink-0">Fee -AUD {Number(cm.fee_amount).toFixed(2)}</span>}
                <span data-testid={`commission-net-${i + 1}`} className="text-nc-green font-bold shrink-0">Net AUD {Number(cm.net_amount ?? cm.total_amount).toFixed(2)}</span>
                <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-1 flex items-center gap-1.5 ${st.cls}`}><st.icon className="w-3 h-3" /> {cm.status}</span>
                {me.role === "admin" && cm.status === "claimable" && (
                  <button data-testid={`commission-payout-${i + 1}`} onClick={() => payout(cm.id)} className="font-mono text-[8px] tracking-[0.15em] uppercase px-2.5 py-1.5 bg-nc-green hover:bg-nc-green/80 text-white transition-colors">Pay Out</button>
                )}
              </div>
            );
          })}
        </div>
        </>)}

        <SalesHUD
          earnings={earnings}
          openLeads={openLeads.length}
          link={`${window.location.origin}/?rec=${me.recruitment_id}`}
          onNavigate={setPageTab}
        />
      </div>
    </div>
  );
}
